// Edit one button's settings — shared by the mobile and desktop shells.
//
// Ports the fields of button_editing_form.dart that live on OutputDetails and
// share one save path:
//
//   • name
//   • All On behaviour   (+ minutes when "Schedule Time")
//   • All Off behaviour  (+ minutes when "Schedule Time")
//   • Key Press Off After
//   • Button State (power-restore)
//
// Two Way Mapping opens from here but SAVES SEPARATELY — it lives in its own
// table, not on this row, so it is not part of this dialog's Save. Schedule
// keeps its own screen and is linked from the actions.
//
// ⚠️ Saving writes the row AND pushes the firmware registers — see
// core/devices/buttonSettings. A row-only write shows the new setting in the
// app while the switch keeps the old behaviour.
//
// ⚠️ `onSaved` IS NOT OPTIONAL IF THE CALLER DRAWS THE BUTTON NAME, and two of
// the three call sites had left it off. This dialog owns no list: the names on
// the tiles behind it come from each screen's own `getOutputs` fetch, which runs
// once on mount. So a rename saved, the register went out, the dialog closed —
// and the tile behind it still read the old name until the page was reloaded,
// which reads as "it did not save". It had saved.
//
// The mobile TouchScreen passed a reload and looked correct; the desktop room
// card and the mobile app-bar menu did not, and looked broken. Same dialog,
// same write, opposite verdicts. If you add a fourth entry point and it shows
// output names anywhere, pass `onSaved`.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Typography, Box, CircularProgress, Alert,
} from '@mui/material';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { useApp } from '../../core/state/AppContext';
import { setOutputConnectedLoad, getOutputsFresh } from '../../core/api/api';
import { LOADS } from '../../core/roomCycle/roomCycle';
import { showToast } from './toast';
import { loadNameIndex } from '../../core/devices/nameIndex';
import {
  findClash, clashMessage, normaliseName, buttonKey,
} from '../../core/devices/alexaNames';
import TwoWayMappingDialog from './TwoWayMappingDialog';
import {
  ALL_ON_OPTIONS, ALL_OFF_OPTIONS, BUTTON_STATE_OPTIONS, SCHEDULE_TIME_OPTION,
  packAllOnOff, unpackAllOnOff, getOutputRow, saveButtonSettings,
} from '../../core/devices/buttonSettings';
import { FIT_SM } from './fitDialog';
import PairedAutomations from './PairedAutomations';

const num = (v) => (Number(v) || 0);

/*
 * ── It has to FIT A LAPTOP, unscrolled ─────────────────────────────────────
 *
 * Seven controls, each with a caption under it, stacked one per row in an `xs`
 * dialog: on a 768px-tall laptop that scrolls, and the Save button is the part
 * below the fold. So the paired settings sit two to a row and every font is
 * stepped down. Nothing is removed — a setting you have to scroll to is still
 * findable, but one you cannot see while reading its neighbour is not
 * comparable, and All On / All Off are read against each other.
 *
 * The grid collapses to one column under `sm`, because the same dialog is the
 * mobile shell's button editor.
 */
const DENSE = {
  '& .MuiInputBase-input': { fontSize: 13 },
  '& .MuiInputLabel-root': { fontSize: 13 },
  '& .MuiSelect-select': { fontSize: 13 },
};

const HELP_SX = { fontSize: 10.5, lineHeight: 1.35, mt: 0.25, display: 'block' };

export default function ButtonSettingsDialog({
  open, uniqueNumber, output, onClose, onSaved, onOpenSchedule,
}) {
  const { userId } = useApp();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [twoWayOpen, setTwoWayOpen] = useState(false);

  const [name, setName] = useState('');
  const [allOn, setAllOn] = useState(0);
  const [allOff, setAllOff] = useState(0);
  const [afterAllOn, setAfterAllOn] = useState(0);
  const [afterAllOff, setAfterAllOff] = useState(0);
  const [keyPressOff, setKeyPressOff] = useState(0);
  const [state, setState] = useState(0);
  // What this output powers (OutputDetails.ConnectedLoad) — the AC–Fan cycle finds
  // a room's AC and fan by it. undefined in the row = the server has no column yet.
  const [connected, setConnected] = useState('');
  const [connectedWas, setConnectedWas] = useState(null);   // null = no column on the server → field hidden

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getOutputRow(uniqueNumber, output);
      const r = (Array.isArray(res?.data) ? res.data[0] : null) || {};
      setRow(r);
      setName(r.OutputName ?? '');
      const { allOn: on, allOff: off } = unpackAllOnOff(r.on_all_on_status);
      setAllOn(on);
      setAllOff(off);
      setAfterAllOn(num(r.OnTimeAfterAllOn));
      setAfterAllOff(num(r.OnTimeAfterAllOff));
      setKeyPressOff(num(r.OnTimeAfterOn));
      setState(Math.min(2, Math.max(0, num(r.PowerOnAction))));
      // ⚠️ FROM THE SERVER, not the row above: the phone reads that row from its
      // offline cache, which has no ConnectedLoad column at all.
      setConnectedWas(null);
      getOutputsFresh(uniqueNumber).then((fr) => {
        const rows = Array.isArray(fr?.data) ? fr.data : [];
        const hit = rows.find((x) => Number(x.Output) === Number(output));
        if (hit && hit.ConnectedLoad !== undefined) {
          const v = String(hit.ConnectedLoad ?? '').toLowerCase();
          setConnected(v);
          setConnectedWas(v);
        }
      }).catch(() => {});
    } catch (e) {
      setError('Could not load this button’s settings.');
    } finally {
      setLoading(false);
    }
  }, [uniqueNumber, output]);

  useEffect(() => { if (open) load(); }, [open, load]);

  /*
   * ⚠️ FOCUS AFTER THE LOAD, NOT ON MOUNT — which is why this is an effect and
   * not `autoFocus` on the field. The field does not exist while the row is
   * being fetched (the dialog shows a spinner instead), so `autoFocus` fires on
   * a control that is about to be replaced and the caret lands nowhere. It also
   * has to run again when the SAME dialog is reopened for a different button.
   *
   * Selected, not just focused: this is a rename box that opens holding the old
   * name, so the first keystroke should replace it rather than append to it.
   */
  const nameRef = useRef(null);
  useEffect(() => {
    // `row` in the guard, not just `loading`: for one render after `open` flips
    // true the fetch has not started yet, so the fields are on screen holding
    // the PREVIOUS button's values. Focusing then would select a name that is
    // about to be replaced.
    if (!open || loading || !row) return;
    const el = nameRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, [open, loading, row]);

  const submit = async () => {
    setSaving(true);
    setError('');

    /*
     * ⚠️ ONLY WHEN THE NAME ACTUALLY CHANGED, and this is the trap that would
     * have made the whole rule unusable. `changes` below always carries
     * `OutputName`, whether or not it was touched — and about half the buttons
     * in a real account already share a name, because the factory defaults
     * ("S1".."S4") repeat on every panel. Checking on every save would block a
     * customer adjusting a TIMER on a button called "S2" over a name they never
     * typed. New duplicates are refused; existing ones are left alone.
     *
     * A null index means the namespace could not be read (offline, endpoint
     * down). Allow the save: Alexa is unreachable then anyway, and refusing
     * would turn a voice nicety into a broken app. See core/devices/nameIndex.
     */
    const next = name.trim();
    const wasNamed = String((row && row.OutputName) || '').trim();
    if (next && normaliseName(next) !== normaliseName(wasNamed)) {
      const index = await loadNameIndex(userId);
      const clash = index && findClash(index, next, buttonKey(uniqueNumber, output));
      if (clash) {
        setSaving(false);
        setError(clashMessage(clash));
        // Put them back in the field they have to fix.
        if (nameRef.current) { nameRef.current.focus(); nameRef.current.select(); }
        return;
      }
    }

    const changes = {
      OutputName: next,
      on_all_on_status: packAllOnOff(allOn, allOff),
      // Only meaningful for "Schedule Time", but sent regardless so switching
      // away from it does not leave a stale delay behind in the register.
      OnTimeAfterAllOn: allOn === SCHEDULE_TIME_OPTION ? num(afterAllOn) : 0,
      OnTimeAfterAllOff: allOff === SCHEDULE_TIME_OPTION ? num(afterAllOff) : 0,
      OnTimeAfterOn: num(keyPressOff),
      PowerOnAction: state,
    };
    const res = await saveButtonSettings(uniqueNumber, output, changes, row, userId);
    if (!res.ok) { setSaving(false); setError(res.error); return; }
    // Its own endpoint, only when changed — a server without the column refuses
    // THIS (said in a toast), never the settings above.
    if (connectedWas !== null && connected !== connectedWas) {
      try {
        await setOutputConnectedLoad(userId, uniqueNumber, output, connected);
      } catch (e) {
        showToast(`"Connected to" not saved: ${e?.response?.data?.error || e?.message || 'failed'}`, { severity: 'error' });
      }
    }
    setSaving(false);
    if (onSaved) onSaved();
    onClose();
  };

  const select = (label, value, onChange, options, help) => (
    <>
      <TextField
        select fullWidth size="small" label={label} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        sx={DENSE}
        SelectProps={{ MenuProps: { PaperProps: { sx: { '& li': { fontSize: 13 } } } } }}
      >
        {options.map((o, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <MenuItem key={i} value={i}>{o}</MenuItem>
        ))}
      </TextField>
      {help && (
        <Typography variant="caption" color="text.secondary" sx={HELP_SX}>
          {help}
        </Typography>
      )}
    </>
  );

  const minutes = (label, value, onChange) => (
    <TextField
      size="small" type="number" label={label} value={value}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ min: 0 }}
      sx={{ ...DENSE, mt: 0.75, width: '100%' }}
    />
  );

  return (
    // `sm`, not `xs`: the two-column grid below needs the width, and trading
    // width for height is the whole point — a laptop is short, not narrow.
    <Dialog open={open} onClose={saving ? undefined : onClose} {...FIT_SM}>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, py: 1.25 }}>
        {`Button Settings — ${output}`}
      </DialogTitle>
      <DialogContent dividers sx={{ py: 1.5 }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading…</Typography>
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

            {/* The name leads, on its own full-width row and holding the
                focus — it is the field this dialog is opened for far more often
                than any of the registers below it. */}
            <TextField
              fullWidth size="small" label="Button name" value={name}
              inputRef={nameRef}
              onChange={(e) => setName(e.target.value)}
              // Enter saves, so a rename is type-and-go rather than
              // type-then-reach-for-the-mouse.
              onKeyDown={(e) => { if (e.key === 'Enter' && !saving) submit(); }}
              sx={DENSE}
            />

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
              mt: 1.5,
              alignItems: 'start',
            }}
            >
              <Box sx={{ minWidth: 0 }}>
                {select('All On', allOn, setAllOn, ALL_ON_OPTIONS,
                  'What this button does when the All On bar is pressed.')}
                {allOn === SCHEDULE_TIME_OPTION
                  && minutes('Turn on for (minutes)', afterAllOn, setAfterAllOn)}
              </Box>

              <Box sx={{ minWidth: 0 }}>
                {select('All Off', allOff, setAllOff, ALL_OFF_OPTIONS,
                  'What this button does when the All Off bar is pressed.')}
                {allOff === SCHEDULE_TIME_OPTION
                  && minutes('Turn off after (minutes)', afterAllOff, setAfterAllOff)}
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <TextField
                  fullWidth size="small" type="number" label="Key press off after (minutes)"
                  value={keyPressOff}
                  onChange={(e) => setKeyPressOff(e.target.value)}
                  inputProps={{ min: 0 }}
                  sx={DENSE}
                />
                <Typography variant="caption" color="text.secondary" sx={HELP_SX}>
                  Switches itself off this many minutes after a press. 0 disables it.
                </Typography>
              </Box>

              {connectedWas !== null && (
                <Box sx={{ minWidth: 0 }}>
                  <TextField
                    select fullWidth size="small" label="Connected to" value={connected}
                    onChange={(e) => setConnected(e.target.value)}
                    sx={DENSE}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { '& li': { fontSize: 13 } } } } }}
                  >
                    {LOADS.map((o) => <MenuItem key={o.value || 'none'} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                  <Typography variant="caption" color="text.secondary" sx={HELP_SX}>
                    What this switch powers. An AC and a fan in one room unlock the AC–Fan cycle.
                  </Typography>
                </Box>
              )}

              <Box sx={{ minWidth: 0 }}>
                {select('Button state', state, setState, BUTTON_STATE_OPTIONS,
                  'What happens when power comes back. "For Router" waits a minute, '
                  + 'then cycles the output so a router restarts cleanly.')}
              </Box>
            </Box>

            {/* Two-way lives in its own table, not on this row, so it saves
                independently of the fields above rather than on Save. */}
            <Button
              size="small"
              startIcon={<SyncAltIcon />}
              onClick={() => setTwoWayOpen(true)}
              sx={{ mt: 1.5, textTransform: 'none', fontSize: 13 }}
            >
              Two Way Mapping…
            </Button>
            {/* Which sensors switch this button, and on what condition. */}
            <PairedAutomations uniqueNumber={uniqueNumber} output={output} locationId={row && row.LocationID} />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ py: 0.75 }}>
        {/* Schedules keep their own screen — this dialog is the way in, so the
            entry point long-press used to provide is not lost. */}
        {onOpenSchedule && (
          <Button size="small" onClick={onOpenSchedule} disabled={saving} sx={{ mr: 'auto' }}>
            Schedules
          </Button>
        )}
        <Button size="small" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button size="small" variant="contained" onClick={submit} disabled={saving || loading}>
          {saving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>

      <TwoWayMappingDialog
        open={twoWayOpen}
        uniqueNumber={uniqueNumber}
        output={output}
        outputName={name}
        onClose={() => setTwoWayOpen(false)}
      />
    </Dialog>
  );
}
